/* eslint-disable camelcase */
import uuid from '../../utils/uuid';
import computed from '../../utils/computed';
import referer from '../../utils/referrer';
import { floor } from '../../utils/math';
import isUndefined from '../utils/is-undefined';
import sourceFormat from './source-extension';
import { STATE_ADPLAYING, METADATA_TYPE_AD_SERVER, METADATA_TYPE_AD_CLIENT, VERSION } from '../constants';

/**
 * Retrieve a players current metadata
 *
 * @param  {iono.Player} player
 * @return {Object}
 */
function getMetadata (player, context) {
  const playlistItem = (isUndefined(context) || isUndefined(context.playlistItem)) ? player.getPlaylistItem() : context.playlistItem;
  return (playlistItem && playlistItem.metadata) || {};
}

/**
 * Retrieve a players current ad
 *
 * @param  {iono.Player} player
 * @param  {Object}      context
 *
 * @return {Object}
 */
function getAd (player, context) {
  return ((context && context.ad) || player.getCurrentAd());
}

/**
 * Retrieve a players current ad metadat
 *
 * @param  {iono.Player} player
 * @param  {Object}      context
 *
 * @return {Object}
 */
function getAdMetadata (player, context) {
  const ad = getAd(player, context);
  return ad && ad.metadata;
}

const contentFields = {
  aduid () {
    return '';
  },
  adslot () {
    return '';
  },
  contentType: function contentFields_contentType (player, context) {
    return getMetadata(player, context).contentType || '';
  },
  creator: function contentFields_creator (player, context) {
    return getMetadata(player, context).creator || '';
  },
  duration: function contentFields_duration (player, context) {
    return player.getDuration();
  },
  format: function contentFields_format (player, context) {
    return sourceFormat(player.getSource()) || '';
  },
  grouping: function contentFields_grouping (player, context) {
    return getMetadata(player, context).grouping || '';
  },
  percent: function contentFields_percent (player, context) {
    if (player.getDuration() <= 0) {
      return 0;
    }
    if (context && typeof context.percent !== 'undefined') {
      return context.percent;
    }
    const percent = floor(player.getPosition() / player.getDuration() * 100, 0);
    return Math.max(0, Math.min(100, percent));
  },
  playbackRate: function contentFields_playbackRate (player, context) {
    return player.getPlaybackRate();
  },
  position: function contentFields_position (player, context) {
    if (context && typeof context.position !== 'undefined') {
      return Math.max(0, context.position);
    }
    return Math.max(0, player.getPosition());
  },
  publishDate: function contentFields_publishDate (player, context) {
    const publishDate = getMetadata(player, context).publishDate;

    if (publishDate instanceof Date) {
      return publishDate.toISOString();
    }

    return '';
  },
  quality: function contentFields_quality (player, context) {
    return player.getQualityLevel() || '';
  },
  title: function contentFields_title (player, context) {
    return getMetadata(player, context).title || '';
  },
  url: function contentFields_url (player, context) {
    return player.getSource().url || '';
  },
  volume: function contentFields_volume (player, context) {
    return player.getVolume();
  },
  lang: function computeField_lang (player, context) {
    return typeof player.getDisplayLanguage !== 'undefined'
      ? player.getDisplayLanguage()
      : player.getLanguage();
  }
};

const advertFields = Object.assign({}, contentFields, {
  aduid (player, context) {
    const metadata = getAdMetadata(player, context);
    return (metadata && metadata.uid) || '';
  },
  adslot (player, context) {
    const ad = getAd(player, context);
    return (ad && ad.slot) || '';
  },
  contentType: function advertFields_contentType (player, context) {
    const metadata = getAdMetadata(player, context);
    return (metadata && metadata.contentType) || '';
  },
  creator: function advertFields_creator (player, context) {
    const metadata = getAdMetadata(player, context);
    return (metadata && metadata.creator) || '';
  },
  duration: function advertFields_duration (player, context) {
    return Math.max(0, player.getAdDuration());
  },
  format: function advertFields_format (player, context) {
    return sourceFormat(player.getAdSource()) || '';
  },
  grouping: function advertFields_grouping (player, context) {
    const metadata = getAdMetadata(player, context);
    return (metadata && metadata.grouping) || '';
  },
  percent: function contentFields_percent (player, context) {
    if (player.getAdDuration() <= 0) {
      return 0;
    }
    if (context && typeof context.percent !== 'undefined') {
      return Math.max(0, Math.min(100, context.percent));
    }
    const percent = floor(player.getAdPosition() / player.getAdDuration() * 100, 0);
    return Math.max(0, Math.min(100, percent));
  },
  position: function advertFields_position (player, context) {
    if (context && typeof context.position !== 'undefined') {
      return Math.max(0, context.position);
    }
    return Math.max(0, player.getAdPosition());
  },
  publishDate: function advertFields_publishDate (player, context) {
    const metadata = getAdMetadata(player, context);
    const publishDate = metadata && metadata.publishDate;

    if (publishDate instanceof Date) {
      return publishDate.toISOString();
    }

    return '';
  },
  quality: function advertFields_quality (player, context) {
    return player.getAdSource().quality || '';
  },
  title: function advertFields_title (player, context) {
    const metadata = getAdMetadata(player, context);
    return (metadata && metadata.title) || '';
  },
  url: function advertFields_url (player, context) {
    return player.getAdSource().url || '';
  }
});

const advertContentTypes = [METADATA_TYPE_AD_SERVER, METADATA_TYPE_AD_CLIENT];
const advertStates = [STATE_ADPLAYING];

/**
 * Get the correct computed fields object by player and context
 *
 * @param  {iono.Player}  player
 * @param  {Object}       context
 *
 * @return {Object}
 */
function contextFields (player, context) {
  if (context && context.ad) {
    return advertFields;
  }

  if (context && context.contentType) {
    if (advertContentTypes.indexOf(context.contentType) !== -1) return advertFields;
    return contentFields;
  }

  if (advertStates.indexOf(player.getState()) !== -1) {
    return advertFields;
  }

  return contentFields;
}

/**
 * Create a computed field function for a player
 *
 * @param  {iono.Player}  player
 * @param  {String}       key
 *
 * @return {Function}
 */
function contextComputeFn (player, key) {
  return function contextComputeFn_contextFields (context) {
    const computedContextFields = contextFields(player, context);

    if (!isUndefined(computedContextFields[key])) {
      return computedContextFields[key](player, context);
    }

    if (context && !isUndefined(context[key])) {
      return context[key];
    }

    return '';
  };
}

/**
 * Create computed fields for a player
 *
 * @param  {iono.Player} player
 *
 * @return {Function}
 */
export default function computedFields (player) {
  const fields = computed({
    // global computed fields
    cb () {
      return String(Date.now());
    },
    gdpr () {
      const options = player.options;
      if (typeof options.gdpr === 'boolean') {
        return options.gdpr ? 'true' : 'false';
      }
      return 'false';
    },
    userConsent () {
      const options = player.options;
      if (typeof options.gdpr === 'object') {
        return options.gdpr.userConsent;
      }
      return '';
    },
    referer () {
      return referer() || '';
    },
    sid (context) {
      return player.getSID() || '';
    },
    uuid (context) {
      return uuid() || '';
    },
    version (context) {
      return VERSION;
    },
    uid (context) {
      return (context && context.uid) || getMetadata(player, context).uid || '';
    },
    muted (context) {
      const muted = (isUndefined(context) || isUndefined(context.muted)) ? player.getMute() : context.muted;
      return muted ? '1' : '0';
    },
    adToken () {
      return fields.context.adToken || '';
    },
    pofToken () {
      return fields.context.pofToken || '';
    },

    // computed fields that need to be context aware (they change how they are computed depending on if an ad is currently playing or not)
    aduid: contextComputeFn(player, 'aduid'),
    adslot: contextComputeFn(player, 'adslot'),
    contentType: contextComputeFn(player, 'contentType'),
    creator: contextComputeFn(player, 'creator'),
    duration: contextComputeFn(player, 'duration'),
    format: contextComputeFn(player, 'format'),
    grouping: contextComputeFn(player, 'grouping'),
    percent: contextComputeFn(player, 'percent'),
    playbackRate: contextComputeFn(player, 'playbackRate'),
    position: contextComputeFn(player, 'position'),
    publishDate: contextComputeFn(player, 'publishDate'),
    quality: contextComputeFn(player, 'quality'),
    title: contextComputeFn(player, 'title'),
    url: contextComputeFn(player, 'url'),
    volume: contextComputeFn(player, 'volume'),
    lang: contextComputeFn(player, 'lang')
  });

  fields.context = {};

  return fields;
};
